import { z } from 'zod';
import { allItemsResponse, optionalCuid, decimalString } from './common';

/**
 * Schemas for the per-project Element line items.
 *
 * Path: `/projects/:projectId/elements`
 *
 * Snapshot semantics:
 * - When created `from-catalog`, the line item copies name/sku/dimensions/
 *   from the request body (price is workshop-set, not catalog-cost).
 * - `catalogItemId` is recorded for traceability and is **immutable** once set.
 * - Subsequent edits to the catalog item do NOT propagate to existing lines.
 *
 * Worker access:
 * - Workers may LIST/GET elements only on projects they are assigned to.
 * - Workers see no `unitPriceNet`/`currency` (financials hidden).
 * - Workers may not mutate (`PROJECT_ITEMS_CREATE/UPDATE/DELETE`).
 *
 * Status locking:
 * - Mutations are forbidden when the parent project is in a terminal status
 *   (`DELIVERED` or `REJECTED`).
 */

const positiveDecimal = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});

const nonNegativeDecimal = decimalString.refine((v) => parseFloat(v) >= 0, {
  message: 'Must be ≥ 0',
});

const dimMm = z.number().int().min(0).max(100_000);
const optionalDimMm = z.number().int().min(0).max(100_000).optional();

const elementCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type ElementCurrencyValue = z.infer<typeof elementCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const elementListItem = z.object({
  id: z.string(),
  projectId: z.string(),
  roomId: z.string().cuid().nullable(),
  areaId: z.string().cuid().nullable(),
  catalogItemId: z.string().nullable(),
  name: z.string(),
  sku: z.string().nullable(),
  widthMm: z.number().int().nullable(),
  heightMm: z.number().int().nullable(),
  depthMm: z.number().int().nullable(),
  material: z.string().nullable(),
  quantity: decimalString,
  coefficient: decimalString,
  unitPriceNet: decimalString.optional(),
  /**
   * Unit price WITHOUT the markup: MANUAL rows repeat `unitPriceNet`; AUTO rows
   * report the engine's catalog-cost sum (`subtotalBaseNet`), absent until a
   * breakdown stored before that field existed is recomputed. Absent when
   * finances are hidden.
   */
  unitPriceBaseNet: decimalString.optional(),
  /**
   * Unit price WITH the markup: `unitPriceNet` times the row coefficient. On
   * AUTO rows the markups already live inside `unitPriceNet`, so the two agree
   * there by construction. Absent when finances are hidden.
   */
  unitPriceFinalNet: decimalString.optional(),
  currency: elementCurrencyEnum.optional(),
  totalNet: decimalString.optional(),
  pricingMode: z.enum(['MANUAL', 'AUTO']),
  materialId: z.string().nullable(),
  hardwareItems: z
    .array(
      z.object({
        id: z.string(),
        hardwareId: z.string(),
        qty: decimalString,
        /** Markup for THIS use of the fitting. See `createElementRequest`. */
        coefficient: decimalString,
      }),
    )
    .optional(),
  computedBreakdown: z.unknown().nullable().optional(),
  pricingError: z.string().optional(),
  orderIndex: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ElementListItem = z.infer<typeof elementListItem>;

export const elementDetails = elementListItem;
export type ElementDetails = z.infer<typeof elementDetails>;

export const listElementsResponse = allItemsResponse(elementListItem);
export type ListElementsResponse = z.infer<typeof listElementsResponse>;

// ----- Manual create (no catalog reference) -----
export const createElementRequest = z.object({
  roomId: optionalCuid(),
  areaId: optionalCuid(),
  name: z.string().trim().min(1).max(200),
  sku: optionalTrim(50),
  widthMm: dimMm,
  heightMm: dimMm,
  depthMm: dimMm,
  material: optionalTrim(100),
  quantity: positiveDecimal.default('1'),
  coefficient: nonNegativeDecimal.default('1.00'),
  unitPriceNet: nonNegativeDecimal.default('0.00'),
  currency: elementCurrencyEnum.default('RON'),
  notes: optionalTrim(2000),
  pricingMode: z.enum(['MANUAL', 'AUTO']).optional(),
  materialId: z.string().cuid().optional().nullable(),
  // Hardware sub-list for AUTO pricing (Σ unitPrice·qty·coefficient).
  //
  // `coefficient` is the markup for THIS use of the fitting. Left out, it
  // starts from the element's own `coefficient` — which is what priced the
  // whole basket before the markup moved onto each line, so a caller that does
  // not know about the split gets the figure it has always got. On an AUTO row
  // the element's own column is then pinned to 1, because a basket of mixed
  // markups has no single number to report.
  hardwareItems: z
    .array(
      z.object({
        hardwareId: z.string().min(1),
        qty: positiveDecimal,
        coefficient: nonNegativeDecimal.optional(),
      }),
    )
    .optional(),
});
export type CreateElementRequest = z.infer<typeof createElementRequest>;

// ----- Create from a CatalogItem (snapshot) -----
//
// Required fields the catalog item may not have are surfaced via `overrides`.
// Unit price MUST be set explicitly here — catalog stores cost, line items
// store sell price, and the API does not assume a markup.
export const createElementFromCatalogRequest = z.object({
  catalogItemId: z.string().min(1),
  roomId: optionalCuid(),
  areaId: optionalCuid(),
  unitPriceNet: nonNegativeDecimal,
  currency: elementCurrencyEnum.optional(),
  quantity: positiveDecimal.default('1'),
  coefficient: nonNegativeDecimal.default('1.00'),
  notes: optionalTrim(2000),
  overrides: z
    .object({
      name: z.string().trim().min(1).max(200).optional(),
      sku: optionalTrim(50),
      widthMm: optionalDimMm,
      heightMm: optionalDimMm,
      depthMm: optionalDimMm,
      material: optionalTrim(100),
    })
    .optional(),
});
export type CreateElementFromCatalogRequest = z.infer<typeof createElementFromCatalogRequest>;

// ----- Update (partial) -----
//
// `catalogItemId` is intentionally not patchable — the snapshot link is
// permanent. `projectId` is taken from the URL.
export const updateElementRequest = z
  .object({
    roomId: z.string().cuid().nullable(),
    areaId: z.string().cuid().nullable(),
    name: z.string().trim().min(1).max(200),
    sku: optionalTrim(50),
    widthMm: dimMm,
    heightMm: dimMm,
    depthMm: dimMm,
    material: optionalTrim(100),
    quantity: positiveDecimal,
    coefficient: nonNegativeDecimal,
    unitPriceNet: nonNegativeDecimal,
    currency: elementCurrencyEnum,
    orderIndex: z.number().int().min(0),
    notes: optionalTrim(2000),
    pricingMode: z.enum(['MANUAL', 'AUTO']),
    materialId: z.string().cuid().nullable(),
    // Replaces the sub-list wholesale. `coefficient` per line as in
    // `createElementRequest`; omitted, the line starts from the element's own.
    hardwareItems: z.array(
      z.object({
        hardwareId: z.string().min(1),
        qty: positiveDecimal,
        coefficient: nonNegativeDecimal.optional(),
      }),
    ),
  })
  .partial();
export type UpdateElementRequest = z.infer<typeof updateElementRequest>;
